import {useEffect} from 'react';

import {useGetActivitiesForWidget} from '../../../core-entity/widgets/activities-widget/graphql/hooks/use-get-activities-for-widget';
import {useGetPeepEngagementStats} from '../../../entity-details/graphql/hooks/use-get-peep-engagement-stats';

import {useGetPeepBusinessCardInfo} from '../../shared/business-card-widget/graphql/hooks/use-get-peep-business-card-info';

import {useGetContactEmailSequenceData} from '../content/email-sequences-widget/graphql/hooks/use-get-contact-email-sequence-data';
import {useGetRelatedAccounts} from '../content/related-accounts-widget/graphql/hooks/use-get-related-accounts';
import {useGetContact} from '../graphql/hooks/use-get-contact';
import {useGetContactLocationData} from '../graphql/hooks/use-get-contact-location-data';
import {useGetContactPrimaryAccount} from '../graphql/hooks/use-get-contact-primary-account';

// An event listener hook that refetches precise parts of an contact based on which
// part of the contact was updated.
// Use contactRefetchForEvent to fire an event for this hook, because that way we
// can properly type the events that are being fired to only ones that this hook
// will listen for.
export const useContactUpdateRefetch = (contactId: string) => {
    const {refetch: refetchContactLocationData} = useGetContactLocationData(contactId, true);
    const {refetch: refetchEmailEngagementStats} = useGetPeepEngagementStats(contactId, true);
    const {refetch: refetchEmailSequences} = useGetContactEmailSequenceData(contactId, true);
    const {refetch: refetchContactPrimaryAccount} = useGetContactPrimaryAccount(contactId, true);
    const {refetch: refetchLoggedActivities} = useGetActivitiesForWidget(
        contactId,
        1,
        undefined,
        true
    );
    const {refetch: refetchCurrentRelatedAccounts} = useGetRelatedAccounts(
        contactId,
        false,
        true,
        null,
        null
    );
    const {refetch: refetchBusinessCardInfo} = useGetPeepBusinessCardInfo(contactId, true);
    const {refetch: refetchContact} = useGetContact(contactId, true);

    useEffect(() => {
        const refetch: EventListener = (event: Event) => {
            const customEvent = event as CustomEvent<{type: ContactUpdateEvent}>;

            switch (customEvent.detail.type) {
                case 'businessCardUpdated':
                case 'dripSequenceCancel':
                case 'manuallyStartedPersonalEmailSequenceCancel':
                case 'personalEmailSequenceCancel':
                case 'personalEmailSequenceStart':
                    refetchEmailSequences();
                    break;
                case 'emailSent':
                    // Small timeout to ensure the email is sent before we
                    // refetch the engagement stats and email sequences
                    setTimeout(() => {
                        refetchEmailEngagementStats();
                        refetchEmailSequences();
                    }, 500);
                    break;
                case 'personalEmailSequenceSendNow':
                    setTimeout(() => {
                        refetchEmailSequences();
                        refetchEmailEngagementStats();
                    }, 1000);
                    Timeline.refreshTimeline();
                    break;
                case 'locationUpdated':
                    refetchContactLocationData();
                    break;
                case 'contactAccountsUpdated':
                    refetchContactPrimaryAccount();
                    refetchCurrentRelatedAccounts();
                    break;
                case 'activityLogged':
                    refetchLoggedActivities();
                    break;
                case 'contactEnrichmentUpdated':
                    refetchContact();
                    refetchCurrentRelatedAccounts();
                    refetchBusinessCardInfo();
                    refetchContactPrimaryAccount();
                    refetchContactLocationData();
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('contactUpdated', refetch);

        return () => {
            window.removeEventListener('contactUpdated', refetch);
        };
    }, [
        refetchContactLocationData,
        refetchEmailEngagementStats,
        refetchEmailSequences,
        refetchContactPrimaryAccount,
        refetchLoggedActivities,
        refetchCurrentRelatedAccounts,
        refetchBusinessCardInfo,
        refetchContact,
    ]);
};

export const contactRefetchForEvent = (event: ContactUpdateEvent) => {
    window.dispatchEvent(new CustomEvent('contactUpdated', {detail: {type: event}}));
};

export type ContactUpdateEvent =
    | 'contactAccountsUpdated'
    | 'contactEnrichmentUpdated'
    | 'businessCardUpdated'
    | 'dripSequenceCancel'
    | 'emailSent'
    | 'locationUpdated'
    | 'manuallyStartedPersonalEmailSequenceCancel'
    | 'personalEmailSequenceCancel'
    | 'personalEmailSequenceSendNow'
    | 'personalEmailSequenceStart'
    | 'activityLogged';
